import React, { useEffect, useState } from "react";
import Layout from "../../Layout";
import { motion } from "framer-motion";
import { createConnectorsApi, editConnectorApi } from "../../../services/axios/AuthApis/AssetManagementApis";
import toast from "../../../utils/notify";

const ConnectorModal = ({
  open,
  onClose,
  categories = [],
  refreshList,
  isEditMode = false,
  selectedConnector,
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    type: "",
  });

  useEffect(() => {
    if (isEditMode && selectedConnector) {
      setFormData({
        name: selectedConnector?.name || "",
        category: selectedConnector?.category_id || "",
        type: selectedConnector?.connector_type || "",
      });
    } else {
      setFormData({ name: "", category: "", type: "" });
    }
  }, [isEditMode, selectedConnector]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        name: formData.name,
        category: formData.category,
        type: formData.type,
      };

      let response
      if (isEditMode) {
        response = await editConnectorApi(selectedConnector?.id, payload);

        toast.success("Connector updated successfully");

       
        // Update only this connector
        refreshList("edit", response.connector);
      } else {
        response = await createConnectorsApi(payload);

        toast.success("Connector created successfully");

        // Add only this connector
        refreshList("create", response.connector);
      }

      setFormData({
        name: "",
        category: "",
        type: "",
      });
      onClose();
    } catch (error) {
      console.error("Error creating connector:", error);
      toast.error(isEditMode?"Failed to edit connector":"Failed to create connector");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl shadow-xl w-full max-w-md p-6"
      >
        <h2 className="text-xl font-semibold text-gray-800 mb-5">
          {isEditMode?"Edit Connector":"Add Connector"}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Connector Name */}
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">
              Connector Name
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Enter connector name"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>

          {/* Category */}
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">
              Category
            </label>
            <select
              value={formData.category}
              name="category"
              required
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            >
              <option value="">Select category</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          {/* Type */}
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">Type</label>
            <select
              value={formData.type}
              name="type"
              required
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            >
              <option value="">Select type</option>
              <option value="APIKEY">API Key</option>
              <option value="OAUTH2">OAuth 2.0</option>
              <option value="SCIM">SCIM</option>
              <option value="BASICAUTH">Basic Auth</option>
            </select>
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 transition disabled:opacity-60"
            >
              {isEditMode
                ? loading
                  ? "Editing..."
                  : "Edit"
                : loading
                ? "Creating..."
                : "Create"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default ConnectorModal;
